import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuthContext } from './AuthContext';

// Define types for our permission system
export type Permission = {
  module_name: string;
  module_route: string;
  action: string;
};

export type Role = {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
};

export type Module = {
  id: string;
  name: string;
  description: string | null;
  route: string | null;
  icon: string | null;
  parent_id: string | null;
  order_index: number;
};

// Type for the role data returned from Supabase
type UserRoleData = {
  role: Role;
};

// Módulo IDs importantes para acesso em toda a aplicação
export const MODULE_IDS = {
  COURSES: '0dea861f-e96a-4d31-8c8c-b1dd7e8af0c3'
};

type PermissionContextType = {
  userPermissions: Permission[];
  userRoles: Role[];
  modules: Module[];
  loading: boolean;
  hasPermission: (moduleName: string, action: string) => boolean;
  hasPermissionById: (moduleId: string, action: string) => boolean;
  refreshPermissions: () => Promise<void>;
  isAdmin: boolean;
};

// Create context with undefined default value
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export function PermissionProvider({ children }: { children: ReactNode }) {
  // Get auth context - this should work since PermissionProvider is nested inside AuthProvider
  const authContext = useAuthContext();
  const user = authContext?.user;

  const [userPermissions, setUserPermissions] = useState<Permission[]>([]);
  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Function to fetch user's permissions
  const fetchUserPermissions = async (): Promise<{ permissions: Permission[], roles: Role[], isAdmin: boolean }> => {
    if (!user?.id) {
      return { permissions: [], roles: [], isAdmin: false };
    }

    try {
      // Fetch user permissions using the stored function
      const { data: permissions, error: permissionsError } = await supabase.rpc(
        'get_user_permissions',
        { p_user_id: user.id }
      );

      if (permissionsError) throw permissionsError;

      // Fetch user roles (only active ones)
      const { data: roleData, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          role:roles (
            id, 
            name, 
            description, 
            is_system
          )
        `)
        .eq('user_id', user.id)
        .eq('is_active', true);

      if (rolesError) throw rolesError;
      
      if (roleData) {
        const roles: Role[] = roleData.map((item: any) => item.role);
        const adminRole = roles.find(role => role.name === 'Admin');
        const isAdminResult = !!adminRole;
        return { permissions: permissions || [], roles, isAdmin: isAdminResult };
      } else {
        return { permissions: permissions || [], roles: [], isAdmin: false };
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return { permissions: [], roles: [], isAdmin: false };
    }
  };

  // Function to fetch all modules
  const fetchModules = async (): Promise<Module[]> => {
    try {
      const { data, error } = await supabase
        .from('modules')
        .select('*')
        .order('order_index');

      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching modules:', error);
      return [];
    }
  };

  // Refresh permissions function that can be called from outside
  const refreshPermissions = async () => {
    setLoading(true);
    try {
      const [permissionData, modulesData] = await Promise.all([
        fetchUserPermissions(), 
        fetchModules()
      ]);

      setUserPermissions(permissionData.permissions);
      setUserRoles(permissionData.roles);
      setIsAdmin(permissionData.isAdmin);
      setModules(modulesData);

    } catch (error) {
      console.error('Failed to refresh permissions:', error);
      toast.error('Falha ao carregar permissões.');
      // Reset state on error
      setUserPermissions([]);
      setUserRoles([]);
      setIsAdmin(false);
      setModules([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has a specific permission
  const hasPermission = (moduleName: string, action: string): boolean => {
    if (isAdmin) return true; // Admin has all permissions
    
    return userPermissions.some(
      perm => perm.module_name === moduleName && perm.action === action
    );
  };

  // Check if user has a specific permission by module ID
  const hasPermissionById = (moduleId: string, action: string): boolean => {
    if (isAdmin) return true; // Admin has all permissions
    
    // Find the module name by ID
    const module = modules.find(m => m.id === moduleId);
    if (!module) return false;
    
    return userPermissions.some(
      perm => perm.module_name === module.name && perm.action === action
    );
  };

  // Load permissions and modules when user changes
  useEffect(() => {
    if (user?.id) {
      refreshPermissions();
    } else {
      // Clear permissions when no user
      setUserPermissions([]);
      setUserRoles([]);
      setIsAdmin(false);
      setModules([]);
      setLoading(false);
    }
  }, [user?.id]);

  return (
    <PermissionContext.Provider
      value={{
        userPermissions,
        userRoles,
        modules,
        loading,
        hasPermission,
        hasPermissionById,
        refreshPermissions,
        isAdmin
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
}

// Custom hook to use the permission context
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}